/* global SideDrawer */

class CartDrawer extends SideDrawer {
  connectedCallback() {
    // Prevent duplicate listeners if the custom element is ever
    // disconnected and connected again.
    if (this.eventsBound) return;

    this.bindEvents();
    this.eventsBound = true;
  }

  bindEvents() {
    // Keep stable function references so listeners can safely be removed.
    this.openDrawerViaEventHandler =
      this.openDrawerViaEventHandler ||
      this.handleDrawerOpenViaEvent.bind(this);

    this.closeDrawerViaEventHandler =
      this.closeDrawerViaEventHandler ||
      this.close.bind(this, null);

    this.cartRefreshEventHandler =
      this.cartRefreshEventHandler ||
      this.cartRefreshHandler.bind(this);

    this.beforeOpenHandler =
      this.beforeOpenHandler ||
      this.scheduleCrossSellInit.bind(this);

    this.afterMergeHandler =
      this.afterMergeHandler ||
      this.scheduleCrossSellInit.bind(this);

    document.addEventListener(
      'dispatch:cart-drawer:open',
      this.openDrawerViaEventHandler
    );

    document.addEventListener(
      'dispatch:cart-drawer:close',
      this.closeDrawerViaEventHandler
    );

    document.addEventListener(
      'dispatch:cart-drawer:refresh',
      this.cartRefreshEventHandler
    );

    /**
     * Cross-sell must still initialise in BOTH situations:
     *
     * 1. Drawer is opened normally from the header.
     * 2. Cart contents have just been refreshed/merged.
     *
     * We therefore keep both events, but funnel them through the
     * same scheduled initializer.
     *
     * During Add to Cart these events can occur synchronously one
     * after another. The timeout below collapses them into one init.
     */
    this.addEventListener(
      'on:cart-drawer:before-open',
      this.beforeOpenHandler
    );

    this.addEventListener(
      'on:cart:after-merge',
      this.afterMergeHandler
    );
  }

  disconnectedCallback() {
    document.removeEventListener(
      'dispatch:cart-drawer:refresh',
      this.cartRefreshEventHandler
    );

    document.removeEventListener(
      'dispatch:cart-drawer:open',
      this.openDrawerViaEventHandler
    );

    document.removeEventListener(
      'dispatch:cart-drawer:close',
      this.closeDrawerViaEventHandler
    );

    this.removeEventListener(
      'on:cart-drawer:before-open',
      this.beforeOpenHandler
    );

    this.removeEventListener(
      'on:cart:after-merge',
      this.afterMergeHandler
    );

    if (this.crossSellInitTimer) {
      clearTimeout(this.crossSellInitTimer);
      this.crossSellInitTimer = null;
    }

    this.eventsBound = false;
  }

  /**
   * Schedule cart drawer image/cross-sell initialization.
   *
   * Multiple calls inside the same interaction are collapsed
   * into a single task.
   */
  scheduleCrossSellInit() {
    if (this.crossSellInitTimer) {
      clearTimeout(this.crossSellInitTimer);
    }

    this.crossSellInitTimer = setTimeout(() => {
      this.crossSellInitTimer = null;

      // Preserve original image loading behaviour.
      theme.manuallyLoadImages(this);

      // Initialise recommendations only once for this event burst.
      this.querySelectorAll('cc-cart-cross-sell').forEach((el) => {
        el.init();
      });
    }, 0);
  }

  /**
   * Handle when the drawer is opened via an event.
   * @param {object} evt - Event object.
   */
  handleDrawerOpenViaEvent(evt) {
    this.open(
      evt.detail
        ? evt.detail.opener
        : null
    );
  }

  /**
   * Trigger refresh of contents.
   */
  cartRefreshHandler() {
    const cartForm = this.querySelector('cart-form');

    if (cartForm) {
      cartForm.refresh();
    }
  }

  /**
   * Update section's cart-form element with new contents.
   * @param {string} html - Whole-section HTML.
   */
  updateFromCartChange(html) {
    const cartForm = this.querySelector('cart-form');

    if (cartForm) {
      cartForm.refreshFromHtml(html);
    }
  }
}

if (!window.customElements.get('cart-drawer')) {
  window.customElements.define('cart-drawer', CartDrawer);
}